import {
  Link as RouterLink,
  useBlocker,
  useNavigate,
  useParams,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { useCallback, useRef, type ComponentType, type MouseEventHandler, type ReactNode } from 'react';

/**
 * The one place the app touches TanStack Router's link/navigate API.
 *
 * The router is defined in code (src/router.tsx) rather than registered
 * globally, so `to` is an ordinary path string here. Route *params* are still
 * fully typed where it matters: each route's component reads them through the
 * route object's own `useParams()`, which infers from the path pattern. This
 * wrapper exists so that if the router's typing ever changes, exactly one file
 * needs touching instead of twenty.
 */

export interface AppLinkProps {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
  replace?: boolean;
  className?: string;
  title?: string;
  target?: string;
  rel?: string;
  'aria-label'?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  children?: ReactNode;
}

export const AppLink = RouterLink as unknown as ComponentType<AppLinkProps>;

export interface GoOptions {
  params?: Record<string, string>;
  replace?: boolean;
  search?: Record<string, string>;
  /** Skip any `useNavigationGuard` in the way. For callers that already asked. */
  ignoreBlocker?: boolean;
}

/** Imperative navigation: `go('/org/$slug', { params: { slug } })`. */
export function useGo(): (to: string, options?: GoOptions) => void {
  const navigate = useNavigate();
  // Memoized because callers put it in effect dependencies. A fresh closure
  // each render makes every one of those effects run after every render, and
  // an effect that navigates then renders, which re-runs it: the page spins at
  // 100% of a core and never paints. `navigate` is stable, so this never goes
  // stale.
  return useCallback(
    (to, options) => {
      const run = navigate as unknown as (input: { to: string; search?: Record<string, string | number> } & Omit<GoOptions, 'search'>) => void;
      // A destination carrying its own query string, the way the inbox
      // stores them ("/v/abc?t=78"): the router wants the path and the
      // search apart, and serializes a numeric string with quotes around
      // it unless it is handed the number.
      const mark = to.indexOf('?');
      if (mark === -1) {
        run({ to, ...options });
        return;
      }
      const search: Record<string, string | number> = { ...options?.search };
      for (const [key, value] of new URLSearchParams(to.slice(mark + 1))) {
        if (key in search) continue;
        search[key] = value !== '' && Number.isFinite(Number(value)) ? Number(value) : value;
      }
      run({ ...options, to: to.slice(0, mark), search });
    },
    [navigate],
  );
}

export interface BackOptions {
  /** Skip any `useNavigationGuard` in the way. For callers that already asked. */
  force?: boolean;
}

/**
 * Back to wherever they came from, and to `fallback` when that is not knowable:
 * a link pasted straight into the address bar has nothing behind it. Used by
 * surfaces you *leave* rather than navigate through, the recorder above all.
 */
export function useGoBack(): (fallback: string, options?: BackOptions) => void {
  const router = useRouter();
  const go = useGo();
  // Stable for the same reason useGo is, and so that anything holding onto it
  // does not see a new function on every render.
  return useCallback(
    (fallback, options) => {
      const ignoreBlocker = options?.force ?? false;
      if (router.history.canGoBack()) router.history.back({ ignoreBlocker });
      else go(fallback, { replace: true, ignoreBlocker });
    },
    [router, go],
  );
}

export interface NavigationGuard {
  /** True while a way out is held up waiting for an answer. */
  blocked: boolean;
  /** Let the held-up navigation through. */
  proceed: () => void;
  /** Call it off and stay put. */
  reset: () => void;
}

/**
 * Hold every way out of the current route while `when` is true: in-app links,
 * imperative navigation, the browser's own back button, and (through the
 * router's beforeunload wiring) closing or reloading the tab.
 *
 * The recorder is why this exists. A take that has not finished uploading lives
 * only in this tab's memory, and a stray back button is not consent to throw it
 * away.
 */
export function useNavigationGuard(when: boolean): NavigationGuard {
  // Read through a ref so the blocker registers once. The recorder re-renders
  // five times a second while it is rolling, and re-subscribing that often
  // would be churn at best and a dropped block at worst.
  const whenRef = useRef(when);
  whenRef.current = when;
  const shouldBlockFn = useCallback(() => whenRef.current, []);

  const blocker = useBlocker({
    shouldBlockFn,
    enableBeforeUnload: shouldBlockFn,
    withResolver: true,
  });

  return {
    blocked: blocker.status === 'blocked',
    proceed: () => blocker.proceed?.(),
    reset: () => blocker.reset?.(),
  };
}

export function usePathname(): string {
  const state = useRouterState() as unknown as { location: { pathname: string } };
  return state.location.pathname;
}

/**
 * Path params for whatever route is matched, without binding to a route id.
 * Each is optional because the caller could be on any route; route components
 * check the one they care about and render a not-found state if it is missing,
 * which cannot actually happen but keeps the types honest.
 */
export function useRouteParams(): Record<string, string | undefined> {
  const run = useParams as unknown as (options: {
    strict: false;
  }) => Record<string, string | undefined>;
  return run({ strict: false });
}
